import { Injectable, NgZone, Optional } from '@angular/core';
import { logError, logFeature } from '@office-start/telemetry-service';
import { KnownFeatureNames } from '@office-start/telemetry-types';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';

import { EncryptionService } from '../service-worker/encryption/encryption.service';
import { InstrumentationParam, InstrumentationService, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { FileType } from '../shared/models/file-item.model';
import { EdgeworthConfigService } from '../shared/services/edgeworth-config.service';
import { EventService } from '../shared/services/event.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { SchedulePriority, SchedulerService } from '../shared/services/scheduler.service';
import { WindowService } from '../shared/services/window.service';
import { AngularEvents } from '../shared/shared-context/angular-events';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { forEachBatch } from '../shared/utilities/for-each-batch.utility';
import { EdgeworthSubstrateDeltaSyncFile, EdgeworthSubstrateFile } from './edgeworth-data.model';
import { getFileType, getNativeUriScheme, getUserActionsDateTime, isEmailAttachment, safeParseDate } from './edgeworth-data.utility';
import { EdgeworthLocalFilesService } from './edgeworth-local-files.service';
import { EdgeworthSubstrateDeltaSyncService } from './edgeworth-substrate-deltasync.service';
import { EdgeworthSubstrateRecentService } from './edgeworth-substrate-recent.service';
import { EdgeworthUiTimestampService } from './edgeworth-ui-timestamp.service';

export interface EdgeworthCacheEntry {
    readonly item: EdgeworthSubstrateDeltaSyncFile | ArrayBuffer;
    readonly useractions_datetime: Date;
    readonly useractions_datetime_inverse_time_in_ms: number;
    readonly id: string;
}

export interface EdgeworthDataSource {
    files: ReadonlyArray<EdgeworthSubstrateDeltaSyncFile>;
    // 'Network' == From Edgeworth Backend
    // 'Cached' == From Edgeworth Client IndexedDB cache
    // 'HwaLocalBridge' == From Native Bridge, only on the WebView2 HWA
    source: 'Network' | 'Cached' | 'HwaLocalBridge';
}

const cacheLogSamplingRate = 0.001;

@Injectable({
    providedIn: 'root'
})
export class EdgeworthDataService {
    public get dataSource(): Observable<EdgeworthDataSource> {
        return this._dataSourceObservable;
    }

    public get hasFailures(): Observable<boolean> {
        return this._hasFailuresObservable;
    }

    public get isInitialStable(): Observable<void> {
        return this._isInitialStableObservable;
    }

    private readonly _allFilesMap: Map<string, EdgeworthSubstrateDeltaSyncFile> = new Map();
    private readonly _dataSource: BehaviorSubject<EdgeworthDataSource> = new BehaviorSubject(undefined);
    private readonly _dataSourceObservable: Observable<EdgeworthDataSource> = this._dataSource.asObservable().pipe(shareReplay(1));
    private readonly _hasFailures: Subject<boolean> = new Subject();
    private readonly _hasFailuresObservable: Observable<boolean> = this._hasFailures.asObservable().pipe(take(1));
    private readonly _isInitialStable: Subject<void> = new Subject();
    private readonly _isInitialStableObservable: Observable<void> = this._isInitialStable.asObservable();
    private readonly uiFeatureEnabled = this.capabilitiesService.capabilities.edgeworthEnabled;
    private syncIntervalHandle: ReturnType<typeof setInterval>;
    private writeInProgress: Promise<boolean> = null;
    private writeScheduled: Promise<boolean> = null;
    private hasDoneBulkRead = false;
    private isTopCacheReadStable = false;
    private isBulkCacheReadStable = false;
    private isRecentFetchStable = false;
    private isSyncStable = false;
    private isInitialized = false;
    private readonly dateProperties = new Set([
        't',
        'useractions_datetime',
        'last_access_datetime',
        'last_modified_datetime',
        'last_shared_datetime',
        'save_for_later_datetime',
        'created_datetime',
        'timestamp',
        'last_action_datetime'
    ]);
    // For the case where we need to re-build the index, direct all network calls to write to this map for a rebuild while continuing to show cached data.
    private readonly secondaryFilesBuffer: Array<EdgeworthSubstrateDeltaSyncFile> = [];
    private shouldRebuildClientCache: boolean;

    constructor(
        private readonly capabilitiesService: CapabilitiesService,
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly edgeworthDeltaSyncService: EdgeworthSubstrateDeltaSyncService,
        private readonly edgeworthRecentService: EdgeworthSubstrateRecentService,
        private readonly instrumentationService: InstrumentationService,
        private readonly windowService: WindowService,
        private readonly localStorageService: LocalStorageService,
        private readonly edgeworthConfigService: EdgeworthConfigService,
        private readonly scheduler: SchedulerService,
        private readonly edgeworthUiTimestampService: EdgeworthUiTimestampService,
        private readonly eventService: EventService,
        private readonly ngZone: NgZone,
        @Optional() private readonly encryptionService: EncryptionService,
        @Optional() private readonly edgeworthLocalFilesService: EdgeworthLocalFilesService | null
    ) {
    }

    public async doBulkRead() {
        if (this.hasDoneBulkRead) {
            return;
        }

        this.loadLocalDocuments();

        this.hasDoneBulkRead = true;

        const params = {};
        let logEvent = false;
        try {
            const start = this.windowService.nativeWindow.performance.now();
            const cacheEntries: ReadonlyArray<EdgeworthCacheEntry> = await this.windowService.nativeWindow.ewIdb.readCache();
            const latency = Math.ceil(this.windowService.nativeWindow.performance.now() - start);
            const files = await this.convertCacheEntriesToFiles(cacheEntries);

            this.updateAllFiles(files, 'Cached');

            // In new user scenario (first visit or subsequent visit with no documents), empty array is returned.
            logEvent = files.length > 0;
            if (logEvent) {
                params['State'] = 'Succeeded';
                params['ActualSize'] = files.length.toString();
                params['Latency'] = latency.toString();
            }
            this.isBulkCacheReadStable = true;
            this.updateIsStable();
        } catch (e) {
            params['State'] = 'Failed';
            params['Error'] = e?.toString?.() || '';
            logEvent = true;
            this.hasDoneBulkRead = false;
            this.isBulkCacheReadStable = true;
            this.updateIsStable();
        }

        if (logEvent && Math.random() < cacheLogSamplingRate) {
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthBulkCacheRead, params);
            // TODO: Remove above Aria logging after fully migrating to OTEL
            logFeature({
                featureName: KnownFeatureNames.Edgeworth,
                eventName: 'EdgeworthBulkCacheRead',
                params
            });
        }
    }

    public startPeriodicSync() {
        if (this.syncIntervalHandle) {
            this.clearPeriodicSync();
        }

        // Not using UpdateContentService because in Office.com we use BaseUpdateContentService class that requires manually pushing updates
        this.syncIntervalHandle = setInterval(() => this.syncDocuments(), this.edgeworthConfigService.config.periodicSyncIntervalInMin * 60000 /* 1 minute */);
    }

    public clearPeriodicSync() {
        if (this.syncIntervalHandle) {
            clearInterval(this.syncIntervalHandle);
            this.syncIntervalHandle = undefined;
        }
    }

    public async syncDocuments(): Promise<void> {
        let hasPluginFailures = false;
        this.isSyncStable = false;

        if (this.shouldRebuildClientCache) {
            this.edgeworthDeltaSyncService.resetTimestamp();
        }

        return new Promise((resolve, reject) => {
            this.edgeworthDeltaSyncService.syncDocuments().subscribe(event => {
                this.updateAllFiles(event.items, 'Network');
                hasPluginFailures = hasPluginFailures || event.hasPluginFailures;

                if (this.shouldRebuildClientCache) {
                    this.secondaryFilesBuffer.push(...event.items);
                }
            }, _error => {
                hasPluginFailures = true;
                this._hasFailures.next(true);
                this.isSyncStable = true;
                this.updateIsStable();
                reject();
            }, async () => {
                this._hasFailures.next(hasPluginFailures);
                this.isSyncStable = true;
                this.updateIsStable();

                if (this.shouldRebuildClientCache) {
                    await this.rebuildCache();
                }

                // Write synced data to client database.
                const writeSucceeded = await this.writeIntoCache();

                if (!hasPluginFailures && writeSucceeded) {
                    this.edgeworthDeltaSyncService.markSyncCompleted();
                }

                this.scheduler.scheduleWork(() => {
                    this.detectDuplicateFiles();
                }, SchedulePriority.VeryLow);

                resolve();
            });
        });
    }

    public init(recentApiAppFilter?: ReadonlySet<FileType>) {
        if (this.isInitialized) {
            return;
        }

        this.shouldRebuildClientCache = !this.isVisitWithinValidTimeWindow();

        if (this.uiFeatureEnabled) {
            const params = {};
            let logEvent = false;

            // Retrieve IDB records if user ID from local storage is the same as current user's PUID
            // If not we just initialize DB. In the future we'll need to handle encrypting/decrypting as well
            const cachedPuid = this.localStorageService.getGlobal(this.windowService.nativeWindow.ewPreloadUserIdStorageKey);
            if (cachedPuid === this.clientStartupConfigService.config.puid) {
                const cacheEntriesPromise = this.windowService.nativeWindow.ewUserActionsPreloadData;
                cacheEntriesPromise.then(cachedEntries => this.convertCacheEntriesToFiles(cachedEntries))
                .then(cachedFiles => {
                    if (cachedFiles?.length > 0) {
                        this.updateAllFiles(cachedFiles, 'Cached');
                        const preloadDuration = this.windowService.nativeWindow.ewUserActionsPreloadDuration;
                        params['State'] = 'Succeeded';
                        params['ActualSize'] = cachedFiles.length.toString();
                        params['Latency'] = preloadDuration?.toString?.() || '';
                        logEvent = true;
                    }
                    this.isTopCacheReadStable = true;
                    this.updateIsStable();
                }).catch(e => {
                    params[InstrumentationParam.State] = 'Failed';
                    params[InstrumentationParam.Error] = e?.toString?.() || '';
                    logEvent = true;
                    this.isTopCacheReadStable = true;
                    this.updateIsStable();
                }).then(() => {
                    if (logEvent && Math.random() < cacheLogSamplingRate) {
                        this.scheduler.scheduleWork(() => {
                            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthCacheRead, params);
                            // TODO: Remove above Aria logging after fully migrating to OTEL
                            logFeature({
                                featureName: KnownFeatureNames.Edgeworth,
                                eventName: 'EdgeworthCacheRead',
                                params
                            });
                        }, SchedulePriority.VeryLow);
                    }
                });
            } else {
                // BUG: In appshell mode sometimes the preload scripts are not added, so the following line will throw if
                // we don't do falsy check for window.ewIdb
                this.windowService.nativeWindow.ewIdb?.initDb(this.clientStartupConfigService.config.puid);
            }
        }

        const recentApiCall = this.edgeworthRecentService.fetchEdgeworthSubstrateRecent(recentApiAppFilter).then(recentData => {
            this.updateAllFiles(recentData.data.files, 'Network');
            this.writeIntoCache(true);
            this.isRecentFetchStable = true;
            this.updateIsStable();
        }, _error => {
            this._hasFailures.next(true);
            this.isRecentFetchStable = true;
            this.updateIsStable();
        });

        // if an app filter is being passed to the /recent call, we know we are on a start page.
        // Being on a start page requires we don't make a full sync request until it has returned, to avoid
        // Substrate throttling.
        const onStartPage = !!recentApiAppFilter;

        if (onStartPage) {
            recentApiCall.then(() => this.syncDocuments());
        } else {
            this.syncDocuments();
        }
        this.isInitialized = true;
    }

    private updateIsStable() {
        if (this.isTopCacheReadStable && this.isBulkCacheReadStable && this.isRecentFetchStable && this.isSyncStable) {
            this._isInitialStable.next();
            this._isInitialStable.complete();
        }
    }

    private updateAllFiles(newFiles: ReadonlyArray<EdgeworthSubstrateDeltaSyncFile | EdgeworthSubstrateFile>, source: 'Network' | 'Cached' | 'HwaLocalBridge') {
        for (const newFile of newFiles) {
            if (newFile.is_deleted) {
                if (this._allFilesMap.has(newFile.id)) {
                    this._allFilesMap.delete(newFile.id);
                }
                continue;
            }

            if (this.shouldIgnore(newFile)) {
                continue;
            }

            const existingFile = this._allFilesMap.get(newFile.id);
            const userActionsDate = getUserActionsDateTime(newFile);
            const userActionsDateInMs = userActionsDate?.getTime();
            const inverseUserActionsDateInMs = userActionsDateInMs ? -userActionsDateInMs : 0;

            if (!existingFile) {
                //Don't add meeting objects with the 'Mail' reference_type first, since they will not have subject populated
                if (newFile.reference_type !== 'Mail') {
                    this._allFilesMap.set(newFile.id, { ...newFile, indexed: false, useractions_datetime: userActionsDate, useractions_datetime_inverse_time_in_ms: inverseUserActionsDateInMs });
                }
            } else {
                // TODO: Alternative solution to determining whether to update an existing file, is to mark a file as "synced" if this function is called
                // from delta sync flow. The following solution is prone to broken scenarios if Recent/DeltaSync APIs change.
                const hasNewDeltaSyncProps = ((newFile as EdgeworthSubstrateDeltaSyncFile).activities && !existingFile.activities) ||
                    ((newFile as EdgeworthSubstrateDeltaSyncFile).subject && !existingFile.subject) ||
                    ((newFile as EdgeworthSubstrateDeltaSyncFile).user_relationship?.last_shared_by_history && !existingFile.user_relationship?.last_shared_by_history);
                const hasNewerStoreTimestamp = newFile.last_store_modified_datetime > existingFile.last_store_modified_datetime;
                const hasNewerRecommendedTimestamp = newFile.last_recommended_modified_datetime > existingFile.last_recommended_modified_datetime;

                let shouldUpdate = hasNewerStoreTimestamp || hasNewerRecommendedTimestamp ||
                    ((newFile.last_recommended_modified_datetime === existingFile.last_recommended_modified_datetime || newFile.last_store_modified_datetime === existingFile.last_store_modified_datetime) && hasNewDeltaSyncProps);

                let updatedFile: EdgeworthSubstrateDeltaSyncFile = existingFile;
                const hasMruSource = newFile.data_source?.includes('Mru');

                const meetingArtifacts = existingFile.meeting_artifacts;
                if (existingFile.type === 'MeetingObject') {
                    newFile.meeting_artifacts?.forEach(newArtifact => {
                        const foundArtifact = meetingArtifacts.find(artifact =>
                            newArtifact.id === artifact.id
                        );

                        if (!foundArtifact) {
                            meetingArtifacts.push(newArtifact);
                            shouldUpdate = true;
                        }
                    });
                }

                if (shouldUpdate) {
                    updatedFile = {
                        ...existingFile,
                        ...newFile,
                        useractions_datetime: userActionsDate,
                        indexed: false,
                        useractions_datetime_inverse_time_in_ms: inverseUserActionsDateInMs,
                        meeting_artifacts: meetingArtifacts
                    };
                    // Overriding SFL datetime explicitly because the property is missing for unpinned items
                    updatedFile.user_relationship.save_for_later_datetime = newFile.user_relationship.save_for_later_datetime;
                    // Overriding is_pinned boolean only if data source is exclusively WSET, since is_pinned can only be set if MRU is a data source
                    if (!hasMruSource) {
                        updatedFile.is_pinned = newFile.is_pinned;
                    }
                    this._allFilesMap.set(existingFile.id, updatedFile);

                    //Finds deleted meetings -- meetings that are present in the existingFile but not in the newFile
                    const deletedMeetingArray = existingFile.meetings?.filter(meetingId => !newFile.meetings?.includes(meetingId));

                    if (deletedMeetingArray) {
                        deletedMeetingArray.forEach(meetingId => {
                            this._allFilesMap.delete(meetingId);
                        });
                    }
                }

                // If new file has Mru as data source, we always override existing Mru-specific properties
                // This is required as a file can potentially be synced twice, once from Wset with a newer timestamp, and once from Mru with older timestamp
                // With existing DeltaSync logic, we would ignore files from older timestamps and leave out the Mru properties, so the overrides below are not
                // included above
                if (hasMruSource) {
                    updatedFile.mru_id = updatedFile.mru_id || newFile.mru_id;
                    updatedFile.mru_type = updatedFile.mru_type || newFile.mru_type;
                    updatedFile.mru_url = updatedFile.mru_url || newFile.mru_url;
                    // is_pinned should only be set (value of true/false) if data source is Mru, and we shouldn't be syncing a file more than twice so always override it
                    if (newFile.is_pinned !== undefined) {
                        updatedFile.is_pinned = newFile.is_pinned;
                    }
                }
            }
        }

        this._dataSource.next(
            {
                files: Array.from(this._allFilesMap.values()),
                source
            }
        );

        this.eventService.broadcastEvent ({
            name: AngularEvents.edgeworthFilesUpdated,
            args: [this._allFilesMap.values()]
        });
    }

    private async writeIntoCache(fromDeltaSync: boolean = false): Promise<boolean> {
        if (!this.uiFeatureEnabled) {
            return;
        }

        if (this.writeInProgress) {
            if (!this.writeScheduled) {
                // A write was attempted while another write was taking place.
                // It will run at the end of the existing write.
                this.writeScheduled = this.writeInProgress.then(() => this.writeIntoCache(fromDeltaSync));
            }

            return this.writeScheduled;
        }

        this.writeScheduled = null;

        this.writeInProgress = new Promise<boolean>(resolveWriteAll => {
            this.scheduler.scheduleWork(() => {
                (async () => {
                    const params = {};

                    // Exclude HWA LocalBridge files from the Edgeworth database / caching mechanism.
                    const allFiles = Array.from(this._allFilesMap.values()).filter(file => file.source !== 'LocalBridgeFile');

                    let writeErrorsDetected = false;

                    try {
                        if (this.encryptionService) {
                            const encryptedFiles = await Promise.all(
                                allFiles.map(file => new Promise<EdgeworthCacheEntry>((resolveEncrypt, rejectEncrypt) => {
                                    this.scheduler.scheduleWork(() => {
                                        const item = { ...file, indexed: false };

                                        this.ngZone.runOutsideAngular(() => {
                                            this.encryptionService.encryptData<EdgeworthSubstrateDeltaSyncFile>(item).then(buf => resolveEncrypt({
                                                id: item.id,
                                                useractions_datetime: item.useractions_datetime,
                                                useractions_datetime_inverse_time_in_ms: item.useractions_datetime_inverse_time_in_ms,
                                                item: buf
                                            })).catch(rejectEncrypt);
                                        });
                                    }, SchedulePriority.VeryLow);
                                })
                            ));

                            await this.doWriteScheduled(encryptedFiles);
                        } else {
                            await this.doWriteScheduled(allFiles.map(file => ({
                                id: file.id,
                                item: { ...file, indexed: false },
                                useractions_datetime: file.useractions_datetime,
                                useractions_datetime_inverse_time_in_ms: file.useractions_datetime_inverse_time_in_ms
                            })));
                        }

                        this.localStorageService.setGlobal(this.windowService.nativeWindow.ewPreloadUserIdStorageKey, this.clientStartupConfigService.config.puid);
                        params['State'] = 'Succeeded';
                    } catch (e) {
                        params['State'] = 'Failed';
                        params['Error'] = e?.toString?.() || '';
                        params['DebugInfo'] = `fromDeltaSync:${fromDeltaSync}, itemCount:${allFiles.length}`;
                        writeErrorsDetected = true;
                    }

                    if (Math.random() < cacheLogSamplingRate) {
                        this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthCacheWrite, params);
                        // TODO: Remove above Aria logging after fully migrating to OTEL
                        logFeature({
                            featureName: KnownFeatureNames.Edgeworth,
                            eventName: 'EdgeworthCacheWrite',
                            params
                        });
                    }

                    // Clear the in-progress write.
                    this.writeInProgress = null;

                    // Complete the in-progress write.
                    resolveWriteAll(!writeErrorsDetected);
                })();

            }, SchedulePriority.VeryLow);
        });

        return this.writeInProgress;
    }

    private shouldIgnore(file: EdgeworthSubstrateDeltaSyncFile | EdgeworthSubstrateFile) {
        if ((file.type === 'Image' && isEmailAttachment(file)) || file.extension === 'ics' || file.type === 'spsite' || file.type === 'WebLink' || file.type === 'Whiteboard' ||
            /* If an AFS local file and there is no native URI scheme, item is not actionable
            In addition, we exclude local files from HWA since we'll use local bridge to get them.
            */
            (file.source === 'LocalFile' && (!getNativeUriScheme(getFileType(file)) || this.capabilitiesService.capabilities.isPwaInUwp)) ||
            (file.type === 'Video' && file.id?.indexOf('STREAMS') === 0) /* TODO: Remove check when server filtering rolled out and cache version bumped */) {
            return true;
        }

        return false;
    }

    private async doWriteScheduled(items: ReadonlyArray<EdgeworthCacheEntry>) {
        const batchedWrites: Array<Promise<void>> = [];

        forEachBatch(items, 10, batchedItems => {
            batchedWrites.push(new Promise<void>((resolveWrite, rejectWrite) => {
                this.scheduler.scheduleWork(() => {
                    this.ngZone.runOutsideAngular(() => {
                        if (!this.windowService.nativeWindow.ewIdb) {
                            rejectWrite(`Missing ewIdb, db version: ${this.windowService.nativeWindow.ewDbVersion}`);
                            return;
                        }
                        this.windowService.nativeWindow.ewIdb.writeIntoCache(batchedItems).then(resolveWrite).catch(rejectWrite);
                    });
                }, SchedulePriority.VeryLow);
            }));
        });

        await Promise.all(batchedWrites);
    }

    private async convertCacheEntriesToFiles(cacheEntries: ReadonlyArray<EdgeworthCacheEntry>) {
        return this.ngZone.runOutsideAngular(() => Promise.all(cacheEntries.map(cacheEntry => this.convertCachedEntry(cacheEntry))));
    }

    private async convertCachedEntry(cacheEntry: EdgeworthCacheEntry) {
        if (cacheEntry.item instanceof ArrayBuffer) {
            const dateReviver = (key: string, value: string) => {
                if (this.dateProperties.has(key)) {
                    return safeParseDate(value);
                }

                return value;
            };

            const decryptedData = await this.encryptionService.decryptData<EdgeworthSubstrateDeltaSyncFile>(new Uint8Array(cacheEntry.item), dateReviver);

            return decryptedData;
        }

        return cacheEntry.item;
    }

    private isVisitWithinValidTimeWindow() {
        const lastVisitTimestamp = this.edgeworthUiTimestampService.getLastUiSyncTimestamp();

        // If they have not visited before, we don't need to do an index rebuild (they should be in a clean state).
        if (!lastVisitTimestamp) {
            return true;
        }

        const now = new Date();
        const differenceInMs = now.getTime() - lastVisitTimestamp.getTime();
        const differenceInDays = differenceInMs / (1000 * 3600 * 24);

        // The valid window is considered if there has been a visit within 15 days.
        const isWithinValidWindow = differenceInDays <= this.edgeworthConfigService.config.reindexThresholdInDays;

        if (!isWithinValidWindow) {
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthRebuildRequired);
            //TODO: Remove above Aria logging after fully migrating to OTEL
            logFeature({
                featureName: KnownFeatureNames.Edgeworth,
                eventName: KnownInstrumentationEvents.EdgeworthRebuildRequired,
                params: {}, //TODO: remove params once it is no longer a required field
                area: 'Edgeworth'
            });
        }

        return isWithinValidWindow;
    }

    private async rebuildCache() {
        await new Promise(resolve => {
            this.eventService.broadcastEvent({
                name: AngularEvents.edgeworthRebuildNeeded,
                args: [ resolve ] // This is run when Trie is deleted
            });
        });

        try {
            await window.ewIdb?.eraseStore();

            this._allFilesMap.clear();
            this.updateAllFiles(this.secondaryFilesBuffer, 'Network');
            this.secondaryFilesBuffer.length = 0;
            this.shouldRebuildClientCache = false;

            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthRebuildCompleted);
            //TODO: Remove above Aria logging after fully migrating to OTEL
            logFeature({
                featureName: KnownFeatureNames.Edgeworth,
                eventName: KnownInstrumentationEvents.EdgeworthRebuildCompleted,
                params: {}, //TODO: Remove params once it is no longer a required field
                area: 'Edgeworth'
            });
        } catch (ex) {
            this.instrumentationService.logErrorEvent(KnownInstrumentationEvents.EdgeworthRebuildError);
            // TODO: Remove above Aria logging after fully migrating to OTEL
            logError({
                eventName:
                KnownInstrumentationEvents.EdgeworthRebuildError,
                errorType: 'Other' //TODO: Either remove this field when errorType is no longer mandatory, or add 'EdgeworthRebuildError' to ErrorType type
            });
        }
    }

    private detectDuplicateFiles() {
        const allFiles = Array.from(this._allFilesMap.values());

        // Duplicate files will have different "id" properties, but the same "mru_id" properties.
        let duplicateFilesCount = 0;

        // This set will contain the mru_ids observed at least once.
        const observedMruIds = new Set<string>();

        allFiles.forEach(file => {
            const mruId = file.mru_id;

            if (!mruId) {
                return;
            }

            if (observedMruIds.has(mruId)) {
                duplicateFilesCount++;
            } else {
                observedMruIds.add(mruId);
            }
        });

        if (duplicateFilesCount > 0) {
            const params = {['ItemCount']: duplicateFilesCount.toString()};
            this.instrumentationService.logGenericEvent(KnownInstrumentationEvents.EdgeworthDuplicateFilesDetected, params);
            //TODO: Remove above Aria logging after fully migrating to OTEL
            logFeature({
                featureName: KnownFeatureNames.Edgeworth,
                eventName: KnownInstrumentationEvents.EdgeworthDuplicateFilesDetected,
                params,
                area: 'Edgeworth'
            });
        }
    }

    private loadLocalDocuments() {
        if (!this.edgeworthLocalFilesService) {
            return;
        }

        this.edgeworthLocalFilesService.getLocalMru().subscribe(files => {
            this.updateAllFiles(files, 'HwaLocalBridge');
        });
    }
}
